import { useState } from 'react';
import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import type { KeycloakClient } from '../../api/keycloak-admin';
import { AppRowMenu } from './AppRowMenu';
import { AppStatusBadge, resolveAppStatus, type DeployedAppSummary } from './AppStatusBadge';
import { AppIcon } from '../AppIcon';

interface AppTableProps {
  clients: KeycloakClient[];
  userCounts: Record<string, number>;
  deployedBySlug: Record<string, DeployedAppSummary>;
  isDark: boolean;
  colors: ColorTheme;
  onRowClick: (id: string) => void;
  onCopied: (message: string) => void;
}

interface AppItemProps {
  client: KeycloakClient;
  userCount: number;
  deployed: DeployedAppSummary | null;
  isDark: boolean;
  colors: ColorTheme;
  onClick: () => void;
  onCopied: (message: string) => void;
}

/** App table on desktop, stacked cards on phones. */
export function AppTable({ clients, userCounts, deployedBySlug, isDark, colors, onRowClick, onCopied }: AppTableProps): React.JSX.Element {
  const headerStyle: React.CSSProperties = {
    padding: '10px 16px', textAlign: 'left', fontFamily: FONTS.heading,
    fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase',
    color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`,
  };

  const items = clients.map(client => ({
    client,
    userCount: userCounts[client.clientId] ?? 0,
    deployed: deployedBySlug[client.clientId] ?? null,
  }));

  return (
    <>
      <div className="vx-desktop-only">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt }}>
              <th style={{ ...headerStyle, width: '50px' }}>Icon</th>
              <th style={headerStyle}>Name</th>
              <th style={headerStyle}>Client ID</th>
              <th style={headerStyle}>Status</th>
              <th style={headerStyle}>Users</th>
              <th style={{ ...headerStyle, width: '48px' }} />
            </tr>
          </thead>
          <tbody>
            {items.map(({ client, userCount, deployed }) => (
              <AppRow
                key={client.id} client={client} userCount={userCount}
                deployed={deployed}
                isDark={isDark} colors={colors}
                onClick={() => onRowClick(client.id)}
                onCopied={onCopied}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="vx-mobile-only" style={{ gap: 0 }}>
        {items.map(({ client, userCount, deployed }) => (
          <AppMobileCard
            key={client.id} client={client} userCount={userCount}
            deployed={deployed}
            isDark={isDark} colors={colors}
            onClick={() => onRowClick(client.id)}
            onCopied={onCopied}
          />
        ))}
      </div>
    </>
  );
}

/** Single clickable app row */
function AppRow({ client, userCount, deployed, isDark, colors, onClick, onCopied }: AppItemProps): React.JSX.Element {
  const [hovered, setHovered] = useState(false);
  const attrs = client.attributes ?? {};
  const isHidden = attrs['vertex.hidden'] === 'true';

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer', borderBottom: `1px solid ${colors.border}`,
        backgroundColor: hovered ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') : 'transparent',
        transition: 'background-color 0.15s',
      }}
    >
      <td style={{ padding: '12px 16px' }}>
        <AppIcon
          icon={attrs['vertex.icon'] ?? ''}
          iconDark={attrs['vertex.icon.dark']}
          name={client.name || client.clientId}
          isDark={isDark}
          size={32}
        />
      </td>
      <td style={{ padding: '12px 16px' }}>
        <NameBlock client={client} isHidden={isHidden} isDark={isDark} colors={colors} />
      </td>
      <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '12px', color: colors.textSecondary }}>
        {client.clientId}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <AppStatusBadge status={resolveAppStatus({ clientEnabled: client.enabled, deployed })} />
      </td>
      <td style={{ padding: '12px 16px', fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>
        {userCount}
      </td>
      <td style={{ padding: '12px 8px', textAlign: 'right' }}>
        <RowMenu client={client} colors={colors} onClick={onClick} onCopied={onCopied} />
      </td>
    </tr>
  );
}

/** Phone-sized card: icon, name, meta, and the existing row menu. */
function AppMobileCard({ client, userCount, deployed, isDark, colors, onClick, onCopied }: AppItemProps): React.JSX.Element {
  const attrs = client.attributes ?? {};
  const isHidden = attrs['vertex.hidden'] === 'true';
  const status = resolveAppStatus({ clientEnabled: client.enabled, deployed });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') onClick(); }}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '12px',
        padding: '14px 16px', cursor: 'pointer',
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <AppIcon
        icon={attrs['vertex.icon'] ?? ''}
        iconDark={attrs['vertex.icon.dark']}
        name={client.name || client.clientId}
        isDark={isDark}
        size={40}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <NameBlock client={client} isHidden={isHidden} isDark={isDark} colors={colors} />
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
          marginTop: '8px', fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary,
        }}>
          <span style={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{client.clientId}</span>
          <AppStatusBadge status={status} />
          <span>{userCount} users</span>
        </div>
      </div>
      <div onClick={event => event.stopPropagation()}>
        <RowMenu client={client} colors={colors} onClick={onClick} onCopied={onCopied} />
      </div>
    </div>
  );
}

/** App title, optional hidden badge, and description. */
function NameBlock({ client, isHidden, isDark, colors }: {
  client: KeycloakClient; isHidden: boolean; isDark: boolean; colors: ColorTheme;
}): React.JSX.Element {
  return (
    <>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
        fontFamily: FONTS.body, fontSize: '14px', fontWeight: 600, color: colors.text,
      }}>
        <span>{client.name || client.clientId}</span>
        {isHidden && <HiddenBadge isDark={isDark} />}
      </div>
      {client.description && (
        <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
          {client.description}
        </div>
      )}
    </>
  );
}

/** Overflow menu shared by the desktop row and the mobile card. */
function RowMenu({ client, colors, onClick, onCopied }: {
  client: KeycloakClient; colors: ColorTheme; onClick: () => void; onCopied: (message: string) => void;
}): React.JSX.Element {
  return (
    <AppRowMenu
      clientId={client.clientId}
      rootUrl={client.rootUrl ?? client.baseUrl ?? null}
      colors={colors}
      onViewDetails={onClick}
      onCopied={onCopied}
    />
  );
}

/** Marks an app that is need-to-know (hidden from the general catalog). */
function HiddenBadge({ isDark }: { isDark: boolean }): React.JSX.Element {
  return (
    <span
      className="vx-hidden-badge vx-track"
      style={{
        padding: '2px 6px', borderRadius: '4px',
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        fontFamily: FONTS.body, fontSize: '9px', color: '#888',
        textTransform: 'uppercase', letterSpacing: '0.1em',
      }}
    >
      Hidden
    </span>
  );
}
